import { useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { api } from '../api'
import { useAktion, useLaden } from '../hooks'
import Meldung from '../komponenten/Meldung'
import Meldungen from '../komponenten/Meldungen'
import Zweitblick from '../komponenten/Zweitblick'
import { datum, heuteIso, menge } from '../format'
import type { ErfassungAntwort, ErfassungVorlage } from '../typen'

/**
 * Freitext-Erfassung fuer alles, was nicht aus SelectLine kommt: Zaehlzettel, Wochenplan,
 * Nachtraege. Der Nutzer waehlt aus, was er erfasst, tippt frei, und bekommt einen Vorschlag,
 * der ueber dieselbe Freigabe laeuft wie ein PDF-Import.
 */
// Je Vorlage ein Beispiel, wie der Zettel aussehen kann - der Hinweistext kommt vom Backend, das
// Beispiel wechselt mit der Auswahl. Der Schlüssel ist der der Vorlage (`VORLAGEN` in freitext.py).
const BEISPIELE: Record<string, string> = {
  zaehlzettel:
    'Frei tippen, so wie es auf dem Zettel steht. Zum Beispiel:\n' +
    '920240 Boden Sanna, 3 Paletten a 1.200 Stück\n' +
    '930240 Wickler Sanna, 18.400 Stück, davon 2.000 gesperrt',
  wochenplan:
    'Die Planung der Woche, so wie sie vom Verkauf kommt. Zum Beispiel:\n' +
    'Sanna Würfel 48 t\n' +
    'Marina Becher 12,5 t\n' +
    'Kokos 10 kg 6 t',
  wareneingang_charge:
    'Lieferung mit Chargen, so wie sie auf dem Lieferschein steht. Zum Beispiel:\n' +
    '920280 Boden Sonja, 24 Paletten, Charge 920280-20260903-1\n' +
    '930280 Wickler Sonja, 60.000 Stück, Charge 930280-20260903-2',
  // „Produktion nachtragen" kommt seit dem 02.11.2026 vom Backend - die Seite zeigt sie wie jede Vorlage, hier nur das Beispiel.
  produktion:
    'Was an einem Tag tatsächlich produziert wurde, in Tonnen - etwa aus der Kladde. Zum Beispiel:\n' +
    '21.10. 10535 Pflanzenfett Stange 3,403 t\n' +
    '22.10. Sanna Würfel 10,3 t',
  korrektur:
    'Was berichtigt wird, mit Vorzeichen - ein Abgang ist negativ. Zum Beispiel:\n' +
    '920240 Boden Sanna: -2.400 Stück, Bruch beim Umlagern\n' +
    '930240 Wickler Sanna: +1.000 Stück, bei der Zählung vergessen',
  frei:
    'Text ohne feste Form, zum Beispiel eine Mail:\n' +
    'Hallo Frau Barth, heute kamen 12 Paletten Boden Sanna (920240) an.\n' +
    'Außerdem sind 2.000 Wickler Sanna wegen Druckfehler gesperrt.',
}

const BEISPIEL_SONST =
  'Frei tippen, so wie es auf dem Zettel steht. Zum Beispiel:\n' +
  '920240 Boden Sanna, 3 Paletten a 1.200 Stück\n' +
  '930240 Wickler Sanna, 18.400 Stück, davon 2.000 gesperrt'

export default function ErfassenSeite() {
  const navigate = useNavigate()
  const [vorlage, setVorlage] = useState('')
  const [text, setText] = useState('')
  const [tag, setTag] = useState(heuteIso())
  const [antwort, setAntwort] = useState<ErfassungAntwort | null>(null)
  const { fehler, arbeitet, ausfuehren } = useAktion()
  const { daten: geladeneVorlagen, fehler: ladeFehler } = useLaden<ErfassungVorlage[]>(() => api.erfassungVorlagen(), [], {
    onGeladen: (werte) => {
      if (werte.length > 0) setVorlage((alt) => alt || werte[0].schluessel)
    },
  })
  const vorlagen = geladeneVorlagen ?? []

  const gewaehlt = vorlagen.find((v) => v.schluessel === vorlage)

  function absenden() {
    setAntwort(null)
    void ausfuehren(async () => setAntwort(await api.erfassungVorschlag(vorlage, text, tag)))
  }

  return (
    <>
      <h2>Von Hand erfassen</h2>

      <div className="leiste">
        <label>
          Was wird erfasst{' '}
          <select value={vorlage} onChange={(e) => setVorlage(e.target.value)}>
            {vorlagen.map((eintrag) => (
              <option key={eintrag.schluessel} value={eintrag.schluessel}>
                {eintrag.titel}
              </option>
            ))}
          </select>
        </label>
        <label>
          Tag <input type="date" value={tag} onChange={(e) => setTag(e.target.value)} />
        </label>
      </div>

      {gewaehlt && <p className="hinweis">{gewaehlt.hinweis}</p>}

      <textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder={BEISPIELE[vorlage] ?? BEISPIEL_SONST}
      />

      <div className="leiste" style={{ marginTop: 10 }}>
        <button className="haupt" disabled={arbeitet || !text.trim()} onClick={absenden}>
          Vorschlag erzeugen
        </button>
        <span className="hinweis">
          Es wird nichts gebucht. Der Vorschlag geht in die Freigabe wie ein PDF-Import.
        </span>
      </div>

      <Meldungen fehler={ladeFehler ?? fehler} />

      {antwort && (
        <>
          <Meldung
            art={antwort.bilanz.unbekannt + antwort.bilanz.widersprueche + antwort.bilanz.unsicher > 0 ? 'warnung' : 'gut'}
            titel="Was aus dem Text wurde"
          >
            {antwort.bilanz.erkannt} Positionen erkannt, {antwort.bilanz.zugeordnet} einem Artikel zugeordnet
            {antwort.bilanz.unbekannt > 0 ? `, ${antwort.bilanz.unbekannt} ohne bekannten Artikel` : ''}
            {antwort.bilanz.unsicher > 0 ? `, ${antwort.bilanz.unsicher} unsicher` : ''}
            {antwort.bilanz.widersprueche > 0
              ? `, ${antwort.bilanz.widersprueche} Widerspruch(e): derselbe Artikel am selben Tag mit verschiedenen Mengen`
              : ''}
            .
            {antwort.nicht_erfasst.length > 0 && (
              <div style={{ marginTop: 4 }}>
                Nicht im Zettel, aber in denselben Gruppen: {antwort.nicht_erfasst.join(', ')}. Wenn das gezählt
                werden sollte, bitte nachtragen.
              </div>
            )}
            {antwort.bilanz.erkannt === 0 && <div style={{ marginTop: 4 }}>Es wurde nichts erkannt - siehe Rückfragen.</div>}
          </Meldung>
          {antwort.rueckfragen.length > 0 && (
            <Meldung art="warnung" titel="Vor dem Buchen zu klären">
              <ul>
                {antwort.rueckfragen.map((frage, index) => (
                  <li key={index}>{frage}</li>
                ))}
              </ul>
            </Meldung>
          )}

          {antwort.positionen.length > 0 && (
            <div className="tabellenrahmen" style={{ marginTop: 12 }}>
              <table>
                <thead>
                  <tr>
                    <th>Artikel</th>
                    <th>Bezeichnung</th>
                    <th>Tag</th>
                    <th>Charge</th>
                    <th className="zahl">Menge</th>
                    <th>Einheit</th>
                    <th>Buchungsart</th>
                    <th>Sicherheit</th>
                    <th>Woraus</th>
                  </tr>
                </thead>
                <tbody>
                  {antwort.positionen.map((position, index) => (
                    <tr key={index}>
                      <td>{position.artikelnummer ?? 'nicht zugeordnet'}</td>
                      <td>{position.bezeichnung ?? ''}</td>
                      <td>{datum(position.tag)}</td>
                      <td>{position.charge ?? ''}</td>
                      <td className="zahl">{menge(position.menge)}</td>
                      <td>{position.einheit ?? ''}</td>
                      <td>{position.bewegungsart ?? ''}</td>
                      <td>
                        <span className={`status ${position.sicherheit === 'sicher' ? 'gebucht' : 'gesperrt'}`}>
                          {position.sicherheit}
                        </span>
                      </td>
                      <td>{position.begruendung ?? ''}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {antwort.import_lauf_id && (
            <div style={{ marginTop: 12 }}>
              <Zweitblick laufId={antwort.import_lauf_id} aktiv />
            </div>
          )}
          {antwort.import_lauf_id && (
            <div className="leiste" style={{ marginTop: 12 }}>
              <button className="haupt" onClick={() => navigate(`/importe/${antwort.import_lauf_id}`)}>
                Zur Freigabe
              </button>
              <span className="hinweis">Der Vorschlag liegt als Import {antwort.import_lauf_id} bereit.</span>
            </div>
          )}
        </>
      )}
    </>
  )
}
